import { useEffect, useMemo, useState } from "react";
import {
  fetchWithTimeout,
  getApiHealth,
  getVetcanApiBaseUrl,
  vetcanUrl,
} from "../lib/vetcanApi";

type CheckState = {
  ok: boolean;
  detail: string;
};

const failHint =
  "If failing, set VITE_VETCAN_API_BASE_URL or fix /api reverse proxy.";

export default function StatusPage() {
  const [loading, setLoading] = useState(true);
  const [apiHealth, setApiHealth] = useState<CheckState>({
    ok: false,
    detail: "Pending",
  });
  const [vetcanHealth, setVetcanHealth] = useState<CheckState>({
    ok: false,
    detail: "Pending",
  });

  const apiBaseUrl = useMemo(() => getVetcanApiBaseUrl(), []);

  useEffect(() => {
    let active = true;

    async function runChecks() {
      setLoading(true);

      const [apiResult, vetcanResult] = await Promise.allSettled([
        getApiHealth(),
        fetchWithTimeout(vetcanUrl("/api/vetcan/health")).then(async (res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }
          const payload = (await res.json()) as { ok?: boolean };
          if (payload.ok !== true) {
            throw new Error("Invalid payload");
          }
          return payload;
        }),
      ]);

      if (!active) return;

      if (apiResult.status === "fulfilled") {
        setApiHealth({
          ok: true,
          detail: `${apiResult.value.service} (${apiResult.value.basePath})`,
        });
      } else {
        setApiHealth({
          ok: false,
          detail: failHint,
        });
      }

      if (vetcanResult.status === "fulfilled") {
        setVetcanHealth({
          ok: true,
          detail: "ok",
        });
      } else {
        setVetcanHealth({
          ok: false,
          detail: failHint,
        });
      }

      setLoading(false);
    }

    void runChecks();

    return () => {
      active = false;
    };
  }, []);

  return (
    <main className="min-h-screen bg-slate-50 px-6 py-16 text-slate-900">
      <section className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-8 shadow-soft">
        <h1 className="text-2xl font-semibold">VetCan Status</h1>
        <p className="mt-3 text-sm text-slate-600">
          Runtime wiring check for embed deployments. No patient content is shown
          on this page.
        </p>

        <div className="mt-6 rounded-xl bg-slate-100 p-4 text-sm">
          <p className="font-semibold">Resolved API base URL</p>
          <p className="mt-1 break-all font-mono text-xs">
            {apiBaseUrl || "/api (same-origin)"}
          </p>
        </div>

        <div className="mt-6 space-y-4 text-sm">
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="font-semibold">GET /api/health</p>
            <p className={`mt-2 ${apiHealth.ok ? "text-emerald-700" : "text-rose-700"}`}>
              {loading ? "Checking..." : apiHealth.ok ? "OK" : "FAIL"}
            </p>
            <p className="mt-1 text-slate-600">{apiHealth.detail}</p>
          </div>

          <div className="rounded-xl border border-slate-200 p-4">
            <p className="font-semibold">GET /api/vetcan/health</p>
            <p className={`mt-2 ${vetcanHealth.ok ? "text-emerald-700" : "text-rose-700"}`}>
              {loading ? "Checking..." : vetcanHealth.ok ? "OK" : "FAIL"}
            </p>
            <p className="mt-1 text-slate-600">{vetcanHealth.detail}</p>
          </div>
        </div>

        <p className="mt-6 text-sm text-slate-600">{failHint}</p>
      </section>
    </main>
  );
}
